import { getPlatform } from "@web/src/app/utils/platform";
import * as React from "react";

export function useHotkeys(configs: HotkeyConfig[], options: { isActive: boolean; activeFilter?: string[] }) {
  const { isActive, activeFilter } = options;

  const invokeCallbackIfMatch = React.useCallback(
    (event: KeyboardEvent, callbackName: "onKeyDown" | "onKeyUp") => {
      const isTextInput = isTargetTextInput(event);
      for (const config of configs) {
        const { allowInInput = false, disabled = false, preventDefault = false, stopPropagation = false } = config;
        const shouldIgnore = (isTextInput && !allowInInput) || disabled;
        const a = getComboFromStr(config.combo);

        if (activeFilter && activeFilter.length > 0) {
          const isFailFilter = !activeFilter.find((f) => {
            const eachFilterCombo = getComboFromStr(f);
            return eachFilterCombo.modifiers === a.modifiers && eachFilterCombo.key === a.key;
          });

          if (isFailFilter) continue;
        }

        const b = getComboFromEvent(event);
        const isMatch = a.modifiers === b.modifiers && a.key === b.key;
        if (shouldIgnore || !isMatch) continue;
        if (preventDefault) event.preventDefault();
        if (stopPropagation) event.stopPropagation();
        config[callbackName]?.(event);
      }
    },
    [configs, activeFilter]
  );

  const handleGlobalKeyDown = React.useCallback(
    (e: KeyboardEvent) => invokeCallbackIfMatch(e, "onKeyDown"),
    [invokeCallbackIfMatch]
  );
  const handleGlobalKeyUp = React.useCallback(
    (e: KeyboardEvent) => invokeCallbackIfMatch(e, "onKeyUp"),
    [invokeCallbackIfMatch]
  );

  React.useEffect(() => {
    if (typeof window === "undefined" || !isActive) return;
    const document = window.document;
    document.addEventListener("keydown", handleGlobalKeyDown);
    document.addEventListener("keyup", handleGlobalKeyUp);
    return () => {
      document.removeEventListener("keydown", handleGlobalKeyDown);
      document.removeEventListener("keyup", handleGlobalKeyUp);
    };
  }, [handleGlobalKeyDown, handleGlobalKeyUp, isActive]);
}

interface HotkeyConfig {
  allowInInput?: boolean;
  combo: string;
  disabled?: boolean;
  label?: React.ReactNode;
  group?: ("select" | "edit")[];
  preventDefault?: boolean;
  stopPropagation?: boolean;
  onKeyDown?(e: KeyboardEvent): any;
  onKeyUp?(e: KeyboardEvent): any;
}

interface MapKeyCodeToStr {
  [code: number]: string;
}

interface IKeyMap {
  [key: string]: string;
}

export function isMac() {
  return getPlatform() === "macos";
}

interface HotkeyCombo {
  key?: string; // key code, undefined when only a modifier key is pressed
  modifiers: number;
}

const keyCodeMap: MapKeyCodeToStr = {
  8: "backspace",
  9: "tab",
  13: "enter",
  20: "capslock",
  27: "esc",
  32: "space",
  33: "pageup",
  34: "pagedown",
  35: "end",
  36: "home",
  37: "left",
  38: "up",
  39: "right",
  40: "down",
  45: "ins",
  46: "del",
  // number keys
  48: "0",
  49: "1",
  50: "2",
  51: "3",
  52: "4",
  53: "5",
  54: "6",
  55: "7",
  56: "8",
  57: "9",
  // alphabet
  65: "a",
  66: "b",
  67: "c",
  68: "d",
  69: "e",
  70: "f",
  71: "g",
  72: "h",
  73: "i",
  74: "j",
  75: "k",
  76: "l",
  77: "m",
  78: "n",
  79: "o",
  80: "p",
  81: "q",
  82: "r",
  83: "s",
  84: "t",
  85: "u",
  86: "v",
  87: "w",
  88: "x",
  89: "y",
  90: "z",
  // punctuation
  106: "*",
  107: "+",
  109: "-",
  110: ".",
  111: "/",
  186: ";",
  187: "=",
  188: ",",
  189: "-",
  190: ".",
  191: "/",
  192: "`",
  219: "[",
  220: "\\",
  221: "]",
  222: "'",
};

const modifierKeyCodeMap: MapKeyCodeToStr = {
  16: "shift",
  17: "ctrl",
  18: "alt",
  91: "meta",
  93: "meta",
  224: "meta",
};

const shiftKeys: IKeyMap = {
  "~": "`",
  "!": "1",
  "@": "2",
  "#": "3",
  $: "4",
  "%": "5",
  "^": "6",
  "&": "7",
  "*": "8",
  "(": "9",
  ")": "0",
  _: "-",
  "+": "=",
  "{": "[",
  "}": "]",
  "|": "\\",
  ":": ";",
  '"': "'",
  "<": ",",
  ">": ".",
  "?": "/",
};

const modifierBitMasks = {
  alt: 1,
  ctrl: 2,
  meta: 4,
  shift: 8,
};

const aliases: Record<string, string> = {
  cmd: "meta",
  command: "meta",
  escape: "esc",
  minus: "-",
  mod: isMac() ? "meta" : "ctrl",
  option: "alt",
  plus: "+",
  return: "enter",
  win: "meta",
};

/**
 * Converts a key combo string into a key combo object. Key combos include
 * zero or more modifier keys, such as `shift` or `alt`, and exactly one
 * action key, such as `A`, `enter`, or `left`.
 *
 * For action keys that require a shift, e.g. `@` or `|`, we inlude the
 * necessary `shift` modifier and automatically convert the action key to the
 * unshifted version. For example, `@` is equivalent to `shift+2`.
 */
const getComboFromStr = (combo: string): HotkeyCombo => {
  const comboPieces = combo.replace(/\s/g, "").toLowerCase().split("+");
  let modifiers = 0;
  let key: string | undefined;
  for (let _piece of comboPieces) {
    if (_piece === "") {
      throw new Error(`Invalid key combo "${combo}"`);
    }

    const piece = aliases[_piece] ?? _piece;

    if (modifierBitMasks[piece as keyof typeof modifierBitMasks]) {
      modifiers += modifierBitMasks[piece as keyof typeof modifierBitMasks]!;
    } else if (shiftKeys[piece] != null) {
      modifiers += modifierBitMasks.shift!;
      key = shiftKeys[piece];
    } else {
      key = piece.toLowerCase();
    }
  }
  return { modifiers, key };
};

function isTargetTextInput(e: KeyboardEvent) {
  const elem = e.target as HTMLElement;
  const editable = elem.closest("input, textarea, [contenteditable=true]");

  if (editable == null) {
    return false;
  }

  // ignore checkboxes, switches, and radio buttons
  if (editable.tagName.toLowerCase() === "input") {
    const inputType = (editable as HTMLInputElement).type;
    if (inputType === "checkbox" || inputType === "radio") {
      return false;
    }
  }

  // ignore read-only fields
  if ((editable as HTMLInputElement).readOnly) {
    return false;
  }

  return true;
}

const getComboFromEvent = (e: KeyboardEvent): HotkeyCombo => {
  let key: string | undefined;
  const { which } = e;
  if (modifierKeyCodeMap[which]) {
    // keep key as undefined
  } else if (keyCodeMap[which]) {
    key = keyCodeMap[which];
  } else {
    key = String.fromCharCode(which).toLowerCase();
  }

  let modifiers = 0;
  if (e.altKey) {
    modifiers += modifierBitMasks.alt;
  }
  if (e.ctrlKey) {
    modifiers += modifierBitMasks.ctrl;
  }
  if (e.metaKey) {
    modifiers += modifierBitMasks.meta;
  }
  if (e.shiftKey) {
    modifiers += modifierBitMasks.shift;
  }

  return { modifiers, key };
};

export type TodoHotkeyConfig = HotkeyConfig;
